import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Avatar,
  Chip,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  FormControl, 
  MenuItem, 
  Select,
  LinearProgress
} from "@mui/material";
import { CiCalendar } from "react-icons/ci";
import { FaAngleRight } from "react-icons/fa6";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CloseIcon from "@mui/icons-material/Close";
import StatusBadge from "components/badge/StatusBadge";
import CustomButton from "components/Button";
import moment from "moment";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { useGetMyTaskHistoryQuery,useGetWorkflowDetailQuery,useApproveWorkflowTaskMutation,useRejectWorkflowTaskMutation } from "store/slices/apis/workFlowSlice";
import { useGetImplementationScheduleDetailQuery, useGetImplementationScheduleDocumentsQuery} from "store/slices/apis/masterScheduleSlice";
import { API_BASE_URL } from "config";
import WorkflowMetaCard from "components/WorkflowMetaCard";
import { enterpriseTokens } from "styles/theme";


const WorkDetail = ({ open, onClose, cardData }) => {

  const dispatch = useDispatch();
  const [comment, setComment] = useState("");
  const [attachments, setAttachments] = useState([]);

  const [approveWorkItem, { isLoading }] = useApproveWorkflowTaskMutation();
  const [rejectWorkItem, { isLRejectLoading }] = useRejectWorkflowTaskMutation();

  const {
    data: historyResponse,
    isLoading: isHistoryLoading,
    error: historyError,
    } = useGetMyTaskHistoryQuery(
        { instanceId: cardData?.workflowInstanceId, type: cardData?.entityType },
        { skip: !cardData?.workflowInstanceId }
  );
  // const {
  //   data: scheduleData,
  //   isLoading: isScheduleLoading,
  //   error: scheduleError,
  //   } = useGetImplementationScheduleDetailQuery(
  //       { id: cardData?.entityId },
  //       { skip: !cardData?.entityId }
  // );
    const {
    data: scheduleData,
    isLoading: isScheduleLoading,
    error: scheduleError,
    } = useGetWorkflowDetailQuery(
        cardData?.workflowId ,
        { skip: !cardData?.workflowId }
  );

   const {
     data: scheduleDocument,
     isLoading: isDocumentsLoading,
     error: documentError,
     } = useGetImplementationScheduleDocumentsQuery(
        { id: cardData?.entityId, type: cardData?.entityType },
        { skip: !cardData?.entityId }
  );



  // historyResponse?.data is assumed to be { workflowSteps: [...] }
  const workflowSteps = historyResponse?.data?.workflowSteps ?? [];
  const futureSteps = historyResponse?.data?.futureSteps ?? [];
  const schedule = scheduleData?.data;
  const documents=scheduleDocument?.data;

  const getFormattedDate = (date) =>
    date ? moment(date).format("DD MMM YYYY") : "";

  const openNotification = (msg, type = "error", duration = 3000) => {
    dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: "alert",
        alert: { color: type },
        close: false,
      })
    );
    setTimeout(() => dispatch(closeSnackbar()), duration);
  };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        console.log("FILES SELECTED:", files); // just to verify

        if (!files.length) return;

        setAttachments((prev) => [...prev, ...files]);

        // allow picking same file again
        e.target.value = "";
};

 const handleRemoveAttachment = (indexToRemove) => {
    setAttachments((prev) =>
        prev.filter((_, index) => index !== indexToRemove)
    );
 };
 
  const handleReject = async () => {
    if (!comment.trim()) {
      openNotification("Please add comments.", "error");
      return;
    }

    try {
        const formData = new FormData();
        formData.append("comment", comment.trim());
        formData.append("type",cardData?.entityType);

        // very important: field name must match [FromForm] List<IFormFile> attachments
        attachments.forEach((file) => {
            formData.append("attachments", file); // repeat same key for multiple files
        });
        if(cardData?.workflowChildStepId)
            formData.append("childStepId", cardData?.workflowChildStepId);

        await rejectWorkItem({
        id: cardData?.workflowId,
        body: formData
        }).unwrap();

      openNotification("Task saved successfully.", "success");
      onClose();
    } catch (err) {
      console.error(err);
      openNotification(
        err?.data?.message ||
          err?.error ||
          "Something went wrong while approving.",
        "error"
      );
    }
  };

  const handleSave = async () => {
    if (!comment.trim()) {
      openNotification("Please add comments.", "error");
      return;
    }

    try {
        const formData = new FormData();
        formData.append("comment", comment.trim());
        formData.append("type",cardData?.entityType);

        // very important: field name must match [FromForm] List<IFormFile> attachments
        attachments.forEach((file) => {
            formData.append("attachments", file); // repeat same key for multiple files
        });
        if(cardData?.workflowChildStepId)
            formData.append("childStepId", cardData?.workflowChildStepId);

        await approveWorkItem({
        id: cardData?.workflowId,
        body: formData
        }).unwrap();

      if(cardData?.entityType=="Schedule Update")
        localStorage.setItem("progress",1);
      openNotification("Approved successfully.", "success");
      onClose();
    } catch (err) {
      console.error(err);
      openNotification(
        err?.data?.message ||
          err?.error ||
          "Something went wrong while approving.",
        "error"
      );
    }
  };
  const [decision, setDecision] = React.useState(""); // "yes" | "no" | ""

  const handleProceed = async () => {
    // If this step supports reject, decision drives the action
    if (cardData?.isRejectAction) {
      if (decision === "yes") return handleSave();   // approve / proceed
      if (decision === "no") return handleReject();  // send back / reject
      return; // safety
    }

    // Normal step (no rejection option)
    return handleSave();
  };

  const getInitials = (name) =>
    (name || "NA")
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const renderAttachmentList = (stepAttachments = []) => {
    if (!stepAttachments.length) return null;

    return (
      <Box
        sx={{
          mt: "6px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          width: "100%",
        }}
      >
        {stepAttachments.map((att, attachmentIndex) => (
          <Box
            key={att.id || `${att.fileName || "attachment"}-${attachmentIndex}`}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: "3px",
              border: "1px solid #ECECEC",
              p: "4px 8px",
              bgcolor: "#FFFFFF",
              gap: 1,
              maxWidth: 320,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 22,
                  height: 26,
                  borderRadius: "4px",
                  bgcolor: "#E7F0FD",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "9px",
                  fontWeight: 600,
                  color: "#0A65E4",
                  fontFamily: "Outfit",
                }}
              >
                {(att.fileType || "file").toUpperCase().slice(0, 4)}
              </Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#2C274A",
                  fontFamily: "Outfit",
                  maxWidth: 200,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {att.fileName || "Attachment"}
              </Typography>
            </Box>

            <Button
              size="small"
              component="a"
              href={`${att.filePath}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: "none",
                fontSize: "11px",
                fontFamily: "Outfit",
                color: "#0A65E4",
                minWidth: "auto",
              }}
            >
              View
            </Button>
          </Box>
        ))}
      </Box>
    );
  };

  const renderChildApprovals = (childStepApprovals = [], parentId) => {
    if (!childStepApprovals.length) return null;

    return (
      <Box
        sx={{
          mt: 1.5,
          pl: 2.5,
          borderLeft: "2px solid #E0E0EA",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {childStepApprovals.map((child, childIndex) => {
          const childUserName =
            child.approverName || child.roleName || "Unknown approver";
          const childStepName = child.childStepName || "";
          const childPrimaryText = child.comment || "Reviewed";
          const childCreatedOn = child.approvedDate;
          const childTimeAgo = childCreatedOn
            ? moment(childCreatedOn).fromNow()
            : "";

          return (
            <Box
              key={child.id || `${parentId}-child-${childIndex}`}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#3A345C",
                  fontFamily: "Outfit",
                }}
              >
                {childPrimaryText}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 400,
                  color: "#8A879C",
                  fontFamily: "Outfit",
                }}
              >
                {childStepName}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 400,
                  color: "#8A879C",
                  fontFamily: "Outfit",
                }}
              >
                {childUserName}
                {childTimeAgo && <> &bull; {childTimeAgo}</>}
                {child.status && <> &bull; {child.status}</>}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderHistoryItem = (item, index) => {
    const userName =
      item.approverRoleName || item.performedBy || "Unknown user";
    const createdOn = item.completedDate || item.createdOn || item.timestamp;
    const timeAgo = createdOn ? moment(createdOn).fromNow() : "";
    const stepAttachments = item.attachments || [];
    const childStepApprovals = item.childStepApprovals || [];
    const workflowStepName = item.workflowStep || "";
    const isRejected = item.actionTaken === "Rejected";
    const primaryText = item.comment || item.action;

    return (
      <Box
        key={item.id || `history-${index}`}
        sx={{
          display: "flex",
          gap: "16px",
          width: "100%",
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: "#140F36",
            color: "#fff",
            fontSize: "12px",
            fontWeight: "bold",
            textTransform: "uppercase",
            mt: "4px",
          }}
        >
          {getInitials(userName)}
        </Avatar>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {isRejected && (
              <CancelOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: "#E53935",
                }}
              />
            )}

            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                fontFamily: "Outfit",
                color: isRejected ? "#E53935" : "#2C274A",
              }}
            >
              {primaryText}
            </Typography>
          </Box>

          <Typography
            sx={{
              mt: "2px",
              fontSize: "12px",
              fontWeight: 400,
              color: "#726F86",
              fontFamily: "Outfit",
            }}
          >
            {workflowStepName}
          </Typography>

          <Typography
            sx={{
              mt: "2px",
              fontSize: "12px",
              fontWeight: 400,
              color: "#726F86",
              fontFamily: "Outfit",
            }}
          >
            {userName}
            {timeAgo && <> &bull; {timeAgo}</>}
          </Typography>

          {renderAttachmentList(stepAttachments)}
          {renderChildApprovals(childStepApprovals, item.id || index)}
        </Box>
      </Box>
    );
  };

  const renderFutureItem = (item, index) => {
    const userName =
      item.approverRoleName ||
      item.roleName ||
      item.assignedToRoleName ||
      item.performedBy ||
      "Upcoming step";
    const workflowStepName =
      item.workflowStep ||
      item.stepName ||
      item.childStepName ||
      item.action ||
      "";
    const primaryText =
      item.comment ||
      item.action ||
      item.description ||
      workflowStepName ||
      "Pending review";
    const expectedDate = item.dueDate || item.expectedDate;

    return (
      <Box
        key={item.id || `future-${index}`}
        sx={{
          display: "flex",
          gap: "16px",
          width: "100%",
        }}
      >
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: "#E7F0FD",
            color: "#0A65E4",
            fontSize: "12px",
            fontWeight: "bold",
            textTransform: "uppercase",
            mt: "4px",
            border: "1px solid #CFE0FB",
          }}
        >
          {getInitials(userName)}
        </Avatar>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pl: 1.5,
            borderLeft: "2px dashed #D6E4F8",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {index === 0 && (
              <Chip
                label="Next Step"
                size="small"
                sx={{
                  height: "22px",
                  bgcolor: "#E7F0FD",
                  color: "#0A65E4",
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: "Outfit",
                }}
              />
            )}
            <StatusBadge status={item.status || "Pending"} />
          </Box>

          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              fontFamily: "Outfit",
              color: "#3A4A66",
            }}
          >
            {primaryText}
          </Typography>

          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              color: "#72809A",
              fontFamily: "Outfit",
            }}
          >
            {workflowStepName}
          </Typography>

          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              color: "#8A879C",
              fontFamily: "Outfit",
            }}
          >
            {userName}
            {expectedDate && <> &bull; Expected by {getFormattedDate(expectedDate)}</>}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getStepTitle = (item) =>
    item?.workflowStep ||
    item?.stepName ||
    item?.childStepName ||
    item?.action ||
    item?.comment ||
    item?.description ||
    "Workflow step";

  const getStepOwner = (item) =>
    item?.approverRoleName ||
    item?.roleName ||
    item?.assignedToRoleName ||
    item?.performedBy ||
    "";

  const currentStageLabel = cardData?.currentStep || "Current step";
  const totalStepCount = workflowSteps.length + futureSteps.length + 1;
  const completedStepCount = workflowSteps.length;
  const workflowProgressValue = totalStepCount
    ? Math.round((completedStepCount / totalStepCount) * 100)
    : 0;
  const forwardProgressSteps = [
    {
      id: "current-step",
      variant: "current",
      title: currentStageLabel,
      owner: cardData?.pendingWith || cardData?.approverRoleName || "",
      caption: "Current stage",
      status: cardData?.workflowStatus || "In Progress",
    },
    ...futureSteps.map((item, index) => ({
      id: item.id || `future-${index}`,
      variant: "future",
      title: getStepTitle(item),
      owner: getStepOwner(item),
      caption:
        index === 0
          ? "Next in line"
          : item?.dueDate || item?.expectedDate
            ? `Expected by ${getFormattedDate(item.dueDate || item.expectedDate)}`
            : "Upcoming",
      status: item.status || "Pending",
    })),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "6px",
          width: "100%",
          margin: "16px",
          maxHeight: "calc(100vh - 32px)",
          border: `1px solid ${enterpriseTokens.lineSoft}`,
          boxShadow: "0 28px 80px rgba(15, 23, 42, 0.22)",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflowY: "auto" }}>
        <Grid container spacing={0}>
          {/* HEADER */}
          <Grid
            size={12}
            sx={{
              p: { xs: "16px", md: "18px 24px" },
              bgcolor: enterpriseTokens.navy,
              position: "sticky",
              top: 0,
              zIndex: 10,
              borderBottom: `1px solid ${enterpriseTokens.navySoft}`,
            }}
          >
            <Grid container spacing={1}>
              <Grid size={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#93C5FD", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "Inter, Outfit, sans-serif" }}>
                      Task review workspace
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.35,
                        fontSize: { xs: "21px", md: "25px" },
                        fontWeight: 700,
                        color: "#FFFFFF",
                        fontFamily: "Inter, Outfit, sans-serif",
                      }}
                    >
                      {cardData?.ProjectName || "Task details"}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                      color: "#E2E8F0",
                      bgcolor: "rgba(255,255,255,0.08)",
                      width: 36,
                      height: 36,
                      border: "1px solid rgba(255,255,255,0.14)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={cardData?.entityType || "Workflow task"} size="small" sx={{ bgcolor: "rgba(255,255,255,0.10)", color: "#E2E8F0", border: "1px solid rgba(255,255,255,0.14)", fontWeight: 750 }} />
                  <Chip label={cardData?.workflowStatus || "In progress"} size="small" sx={{ bgcolor: "#DBEAFE", color: enterpriseTokens.primaryDark, fontWeight: 600 }} />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* SUMMARY CARD */}
          <Grid size={12} sx={{ p: { xs: "12px", md: "20px 24px 16px" }, bgcolor: "#F4F7FB" }}>
            <Box
              sx={{
                border: `1px solid ${enterpriseTokens.lineSoft}`,
                borderRadius: "3px",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 0,
                bgcolor: "#FFFFFF",
                overflow: "hidden",
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Box
                sx={{
                  width: { xs: "100%", sm: "6px" },
                  minHeight: { xs: "6px", sm: "160px" },
                  bgcolor: enterpriseTokens.primary,
                }}
              />

              <Box sx={{ p: { xs: 2, md: 2.25 }, width: "100%" }}>
                {/* Row 1 */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                  }}
                >
                  <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: enterpriseTokens.primary,
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: enterpriseTokens.navy,
                          fontFamily: "Inter, Outfit, sans-serif",
                          whiteSpace: { xs: "normal", md: "nowrap" },
                        }}
                      >
                        {cardData?.ProjectName || ""}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#433F5E",
                          fontFamily: "Outfit",
                        }}
                      >
                        Description: {schedule?.workflowConfigurationName || ""}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: { xs: "4px", md: "12px" },
                      alignItems: { xs: "flex-start", md: "center" },
                      mt: { xs: "10px", md: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#433F5E",
                        fontFamily: "Outfit",
                      }}
                    >
                      Initiated On
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        gap: "8px",
                        alignItems: "center",
                        backgroundColor: "#E7E7EB",
                        p: "2px 6px",
                        borderRadius: "5px",
                      }}
                    >
                      <CiCalendar size={16} color="#5B5772" />
                      <Typography
                        sx={{
                          color: "#5B5772",
                          fontWeight: 600,
                          fontSize: "12px",
                          fontFamily: "Outfit",
                        }}
                      >
                        {getFormattedDate(cardData?.pendingFrom || cardData?.createdDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Row 2 */}
                <Box
                  sx={{
                    mt: "12px",
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                  }}
                >
                  <Box sx={{ display: "flex", gap: "5px", alignItems: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#433F5E",
                        fontFamily: "Outfit",
                      }}
                    >
                      Type:
                    </Typography>
                    <Chip
                      sx={{
                        height: "17px",
                        borderRadius: "5px",
                        backgroundColor: "#D0CFD7",
                        px: "6px",
                      }}
                      label={
                        <Box
                          sx={{
                            fontFamily: "Outfit",
                            fontWeight: 600,
                            fontSize: "10px",
                            color: "#433F5E",
                          }}
                        >
                          {cardData?.entityType || ""}
                        </Box>
                      }
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      gap: { xs: "4px", md: "12px" },
                      alignItems: { xs: "flex-start", md: "center" },
                      mt: { xs: "10px", md: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#433F5E",
                        fontFamily: "Outfit",
                      }}
                    >
                      Due On
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        gap: "8px",
                        alignItems: "center",
                        backgroundColor: "#E7E7EB",
                        p: "2px 6px",
                        borderRadius: "5px",
                      }}
                    >
                      <CiCalendar size={16} color="#5B5772" />
                      <Typography
                        sx={{
                          color: "#5B5772",
                          fontWeight: 600,
                          fontSize: "12px",
                          fontFamily: "Outfit",
                        }}
                      >
                        {getFormattedDate(cardData?.dueDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Row 3 */}
                <Box
                  sx={{
                    mt: "15px",
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "5px",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#433F5E",
                          fontFamily: "Outfit",
                        }}
                      >
                        Initiated By:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#433F5E",
                          fontFamily: "Outfit",
                        }}
                      >
                        {cardData?.createdBy || "Not available"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "12px",
                      alignItems: "center",
                      mt: { xs: "10px", md: 0 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#433F5E",
                        fontFamily: "Outfit",
                      }}
                    >
                      Workflow status
                    </Typography>
                    <StatusBadge status={cardData?.workflowStatus || ""} />
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: "18px",
                    p: "14px",
                    borderRadius: "3px",
                    bgcolor: "#F8FAFC",
                    border: `1px solid ${enterpriseTokens.lineSoft}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", md: "center" },
                      flexDirection: { xs: "column", md: "row" },
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "15px",
                          fontWeight: 600,
                          color: "#2C274A",
                          fontFamily: "Outfit",
                        }}
                      >
                        Workflow Progress
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#726F86",
                          fontFamily: "Outfit",
                          mt: 0.4,
                        }}
                      >
                        {completedStepCount} of {totalStepCount} stages completed
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: "12px",
                        alignItems: { xs: "flex-start", md: "center" },
                        flexDirection: { xs: "column", md: "row" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#433F5E",
                          fontFamily: "Outfit",
                        }}
                      >
                        Current Stage:
                      </Typography>
                      <StatusBadge status={cardData?.currentStep || ""} />
                    </Box>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={workflowProgressValue}
                    sx={{
                      mt: 1.5,
                      height: 8,
                      borderRadius: 999,
                      bgcolor: "#E8ECF4",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        backgroundColor: enterpriseTokens.primary,
                      },
                    }}
                  />

                  <Box
                    sx={{
                      mt: 1.75,
                      display: "flex",
                      alignItems: "stretch",
                      overflowX: "auto",
                      pb: 1,
                      pr: 1,
                      scrollbarWidth: "thin",
                      "&::-webkit-scrollbar": { height: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#D5DCEA",
                        borderRadius: 999,
                      },
                    }}
                  >
                    {forwardProgressSteps.map((step, index) => {
                      const isCurrent = step.variant === "current";
                      const isNext = step.variant === "future" && index === 1;
                      const isFuture = step.variant === "future";

                      return (
                        <React.Fragment key={step.id || index}>
                          <Box
                            sx={{
                              minWidth: { xs: 220, md: 250 },
                              maxWidth: { xs: 220, md: 250 },
                              borderRadius: "3px",
                              border: isCurrent
                                ? "1px solid #A8B8FF"
                                : isNext
                                  ? "1px dashed #BFD2F6"
                                  : "1px dashed #D7DFEC",
                              bgcolor: isCurrent ? enterpriseTokens.surfaceTint : "#FFFFFF",
                              p: "12px 14px",
                              boxShadow: isCurrent ? "0 6px 18px rgba(29, 78, 216, 0.10)" : "none",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  letterSpacing: "0.3px",
                                  color: isCurrent ? "#6054BB" : "#72809A",
                                  textTransform: "uppercase",
                                  fontFamily: "Outfit",
                                }}
                              >
                                {isCurrent ? "Current" : isNext ? "Next" : "Upcoming"}
                              </Typography>
                              <StatusBadge status={step.status || (isCurrent ? "In Progress" : "Pending")} />
                            </Box>

                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#2C274A",
                                lineHeight: 1.4,
                                fontFamily: "Outfit",
                              }}
                            >
                              {step.title}
                            </Typography>

                            {step.owner && (
                              <Typography
                                sx={{
                                  mt: 0.6,
                                  fontSize: "11px",
                                  fontWeight: 500,
                                  color: "#5F6B84",
                                  fontFamily: "Outfit",
                                }}
                              >
                                {step.owner}
                              </Typography>
                            )}

                            <Typography
                              sx={{
                                mt: 0.8,
                                fontSize: "11px",
                                fontWeight: 400,
                                color: "#8A879C",
                                fontFamily: "Outfit",
                              }}
                            >
                              {step.caption}
                            </Typography>
                          </Box>

                          {index < forwardProgressSteps.length - 1 && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "#B6C0D4",
                                px: 0.5,
                              }}
                            >
                              <FaAngleRight size={14} />
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>

                {/* Row 4 */}
                <Box
                  sx={{
                    mt: "8px",
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                  }}
                >
                  {!isDocumentsLoading && !documentError && documents?.length > 0 && (
                    <Box
                      sx={{
                        mt: "6px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        width: "100%",
                      }}
                    >
                      {documents.map((att) => (
                        <Box
                          key={att.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderRadius: "3px",
                            border: "1px solid #ECECEC",
                            p: "4px 8px",
                            bgcolor: "#FFFFFF",
                            gap: 1,
                            maxWidth: 320,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 22,
                                height: 26,
                                borderRadius: "4px",
                                bgcolor: "#E7F0FD",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "9px",
                                fontWeight: 600,
                                color: "#0A65E4",
                                fontFamily: "Outfit",
                              }}
                            >
                              {(att.fileType || "file").toUpperCase().slice(0, 4)}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontWeight: 500,
                                color: "#2C274A",
                                fontFamily: "Outfit",
                                maxWidth: 200,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {att.fileName || "Attachment"}
                            </Typography>
                          </Box>

                          <Button
                            size="small"
                            component="a"
                            href={`${API_BASE_URL}${att.filePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textTransform: "none",
                              fontSize: "11px",
                              fontFamily: "Outfit",
                              color: "#0A65E4",
                              minWidth: "auto",
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Row 3 */}
                <WorkflowMetaCard
                  cardData={scheduleData?.data}
                  StatusBadge={StatusBadge}
                  title="Metadata"
                  columns={2}
                  hiddenKeys={["DesignTrackerId"]} // optional
                  labelMap={{
                    DrgNo: "Drawing No",
                    TargetDate: "Target Date",
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* BODY: HISTORY + ACTION */}
          <Grid size={12} sx={{ bgcolor: "#F4F7FB" }}>
            <Grid
              container
              rowSpacing={{ xs: 2, md: 0 }}
              columnSpacing={3}
              sx={{ p: { xs: "0 12px 16px", md: "0 24px 24px" } }}
            >
              {/* LEFT: HISTORY */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                    alignItems: "flex-start",
                    bgcolor: "#FFFFFF",
                    border: `1px solid ${enterpriseTokens.lineSoft}`,
                    borderRadius: "3px",
                    p: 2,
                    minHeight: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: enterpriseTokens.navy,
                      fontFamily: "Inter, Outfit, sans-serif",
                    }}
                  >
                    Document Activity
                  </Typography>
                  <Box
                    sx={{
                      p: "6px 10px",
                      bgcolor: enterpriseTokens.primarySoft,
                      borderRadius: "999px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#0A65E4",
                        fontFamily: "Outfit",
                      }}
                    >
                      Workflow History
                    </Typography>
                  </Box>

                <Box
                  sx={{
                    mt: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                    alignItems: "flex-start",
                    //maxHeight: 420,
                    pr: 1,
                    overflowY: "auto",
                    width: "100%",
                    maxHeight: 430,
                  }}
                >
                  {/* Loading */}
                  {isHistoryLoading && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#726F86",
                        fontFamily: "Outfit",
                      }}
                    >
                      Loading history...
                    </Typography>
                  )}

                  {/* Error */}
                  {historyError && !isHistoryLoading && (
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "#FF383C",
                        fontFamily: "Outfit",
                      }}
                    >
                      Failed to load history.
                    </Typography>
                  )}

                  {/* Empty */}
                  {!isHistoryLoading &&
                    !historyError &&
                    workflowSteps.length === 0 &&
                    futureSteps.length === 0 && (
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#726F86",
                          fontFamily: "Outfit",
                        }}
                      >
                        No workflow history available for this task yet.
                      </Typography>
                    )}

                  {!isHistoryLoading &&
                    !historyError &&
                    workflowSteps.length > 0 &&
                    [...workflowSteps].reverse().map(renderHistoryItem)}


                </Box>
                </Box>
              </Grid>

              {/* RIGHT: REVIEW & PROCEED */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    alignItems: "flex-start",
                    bgcolor: "#FFFFFF",
                    border: `1px solid ${enterpriseTokens.lineSoft}`,
                    borderRadius: "3px",
                    p: 2,
                    minHeight: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: enterpriseTokens.navy,
                      fontFamily: "Inter, Outfit, sans-serif",
                    }}
                  >
                    Review &amp; Proceed
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#726F86",
                      fontFamily: "Inter, Outfit, sans-serif",
                    }}
                  >
                    
                    {cardData?.currentStep ||""}
                    {/* This step has a single action:{" "}
                    <strong>Save &amp; Proceed</strong>. */}
                  </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "14px",
                    mt: "18px",
                    alignItems: "flex-start",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: enterpriseTokens.primary,
                      color: "#fff",
                      fontSize: "13px",
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      flex: 1,
                    }}
                  >
                    <TextField
                      label="Comments"
                      placeholder="Add your comments for this action..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      multiline
                      minRows={4}
                      fullWidth
                      InputLabelProps={{
                        sx: {
                          fontFamily: "Inter, Outfit, sans-serif",
                          fontSize: "12px",
                        },
                      }}
                      inputProps={{
                        sx: {
                          fontFamily: "Inter, Outfit, sans-serif",
                          fontSize: "12px",
                        },
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#2C274A",
                          fontFamily: "Outfit",
                        }}
                      >
                        Attachments (optional)
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          variant="outlined"
                          component="label"
                          sx={{
                            textTransform: "none",
                            fontSize: "13px",
                            fontFamily: "Inter, Outfit, sans-serif",
                            borderRadius: "3px",
                            px: 2,
                            py: 0.5,
                          }}
                        >
                          Upload file
                          <input type="file" hidden multiple onChange={handleFileChange} />
                        </Button>

                        {attachments.map((file, index) => (
                            <Box
                                key={file.name + index}
                                sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                backgroundColor: "#F5F5F5",
                                borderRadius: "3px",
                                px: 2,
                                py: 0.5,
                                }}
                            >
                                <Typography
                                sx={{
                                    fontSize: "12px",
                                    fontFamily: "Outfit",
                                    color: "#433F5E",
                                    maxWidth: 200,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                                >
                                {file.name}
                                </Typography>
                                <Button
                                size="small"
                                onClick={() => handleRemoveAttachment(index)}
                                sx={{
                                    textTransform: "none",
                                    fontSize: "11px",
                                    minWidth: "auto",
                                    color: "#F15744",
                                }}
                                >
                                Remove
                                </Button>
                            </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box
  sx={{
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    mt: { xs: "24px", md: "32px" },
    mx: -2,
    mb: -2,
    px: 2,
    py: "14px",
    flexDirection: { xs: "column", md: "row" },
    gap: "14px",
    position: "sticky",
    bottom: 0,
    zIndex: 8,
    bgcolor: "#F8FAFC",
    borderTop: `1px solid ${enterpriseTokens.lineSoft}`,
  }}
>
  {/* Right side actions */}
  <Box
    sx={{
      display: "flex",
      gap: "14px",
      alignItems: { xs: "stretch", md: "flex-end" },
      flexDirection: { xs: "column", md: "row" },
      width: { xs: "100%", md: "auto" },
    }}
  >
    {/* Decision dropdown only when reject action is allowed */}
    {cardData?.isRejectAction && (
      <FormControl
        size="small"
        sx={{
          width: { xs: "100%", md: 300 },
        }}
      >
        <Typography
          sx={{
            color: enterpriseTokens.slate,
            fontFamily: "Inter, Outfit, sans-serif",
            fontSize: "12px",
            fontWeight: 600,
            mb: 0.75,
          }}
        >
          Review Outcome
        </Typography>
        <Select
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: "3px",
            fontFamily: "Inter, Outfit, sans-serif",
            fontSize: "12px",
            height: 46,
            bgcolor: "#fff",
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography sx={{ color: "#726F86", fontFamily: "Outfit", fontSize: "12px" }}>
                  Select outcome
                </Typography>
              );
            }
            return selected === "yes" ? "Proceed - no further comments" : "Send back for corrections";
          }}
        >
          <MenuItem value="yes">Proceed - no further comments</MenuItem>
          <MenuItem value="no">Send back for corrections</MenuItem>
        </Select>
      </FormControl>
    )}

    {/* Single Proceed button */}
    <Button
      onClick={handleProceed}
      disabled={
        isLoading || isLRejectLoading || (cardData?.isRejectAction && !decision)
      }
      sx={{
        backgroundColor: enterpriseTokens.primary,
        color: "#fff",
        fontFamily: "Inter, Outfit, sans-serif",
        fontWeight: 600,
        fontSize: "12px",
        lineHeight: "100%",
        letterSpacing: 0,
        width: { xs: "100%", md: "auto" },
        px: "20px",
        py: "15px",
        borderRadius: "3px",
        textTransform: "none",
        "&:hover": { backgroundColor: enterpriseTokens.primaryDark },
        "&.Mui-disabled": { opacity: 0.6, color: "#fff" },
      }}
    >
      {isLoading || isLRejectLoading ? "Saving..." : "Submit"}
    </Button>
  </Box>
</Box>
                </Box>

                {/* <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: { xs: "28px", md: "48px" },
                    flexDirection: { xs: "column-reverse", md: "row" },
                    gap: { xs: "16px", md: 0 },
                  }}
                >
                    <Button
                      onClick={onClose}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#F15744",
                        fontFamily: "Outfit",
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      Close
                    </Button>
                  {cardData?.isRejectAction && (<Button 
                    variant="outlined"
                    component="label"
                    label={isLRejectLoading ? "Saving..." : "Send Back"}
                    onClick={handleReject}
                      sx={{
                          textTransform: "none",
                          color: "#00344C",
                          fontSize: "12px",
                          lineHeight: "100%",
                          fontFamily: "Outfit, sans-serif",
                          borderRadius: "25px",
                          fontWeight: 500,
                          px: "20px",
                          py: "15px",
                      }}>
                      Send Back
                  </Button>)}
                  <Box
                    sx={{
                      display: "flex",
                      gap: "20px",
                      alignItems: "center",
                      flexDirection: { xs: "column", md: "row" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    <CustomButton
                      label={isLoading ? "Saving..." : "Proceed Ahead"}
                      color="primary"
                      disabled={isLoading}
                      onClick={handleSave}
                      sx={{
                        backgroundColor: "#00344C",
                        fontFamily: "Outfit",
                        fontWeight: 500,
                        fontSize: "12px",
                        lineHeight: "100%",
                        letterSpacing: 0,
                        width: { xs: "100%", md: "auto" },
                      }}
                    />
                  </Box>
                </Box> */}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default WorkDetail;
